import type { DailyCostBreakdown } from '@app/shared';

/**
 * Pure transform: the daily-cost-breakdown contract → a stacked-bar chart model. Kept
 * side-effect-free and framework-agnostic so it's unit-tested directly (the repo's web
 * test runner is node-only; component/flow coverage lives in Playwright E2E).
 *
 * One series per agent (in the contract's stable `agents` order — color follows the
 * entity, never its rank), one x-axis tick per UTC day. The Quick-chat bucket
 * (`agentId === null`) is a series like any other so daily totals stay complete.
 *
 * Categorical color is a fixed 8-hue palette (never cycled): if a workspace has more
 * agents than that, the smallest spenders past the cap fold into a single **"Other"**
 * series so no two agents ever share a confusable hue.
 */

/** Distinct categorical slots available before the tail folds into "Other". */
export const MAX_SERIES = 8;
/** Sentinel series-id for the folded "Other" bucket (agent ids are uuids — no collision). */
export const OTHER_SERIES_ID = 'other';

export interface CostSeries {
  /** Stable identity — an agent id, null for Quick-chat, or `OTHER_SERIES_ID` for the fold. */
  agentId: string | null;
  label: string;
  /** One value per day, aligned to `xLabels` (0 for a day the agent spent nothing). */
  data: number[];
}

export interface CostChartModel {
  /** Raw `YYYY-MM-DD` day for each column (formatting for display is the view's job). */
  dates: string[];
  /** Short display labels for the x-axis (e.g. `Jul 15`). */
  xLabels: string[];
  series: CostSeries[];
  /** Grand total across the whole window — powers the header figure. */
  totalUsd: number;
  /** True when there is no cost anywhere in the window (drives the empty state). */
  isEmpty: boolean;
}

/** `YYYY-MM-DD` → a short, locale-stable `MMM D` label (parsed as a UTC day). */
export function formatDayLabel(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * Format a USD amount. Costs can be tiny (sub-cent, #195), so small non-zero values
 * keep more precision: `$0.0123` under a cent, `$1.23` otherwise, `$0.00` for zero.
 */
export function formatUsd(n: number): string {
  if (n === 0) return '$0.00';
  if (Math.abs(n) < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

/** Element-wise sum of same-length arrays (used to fold the tail into "Other"). */
function addInto(target: number[], add: number[]): number[] {
  return target.map((v, i) => v + (add[i] ?? 0));
}

/** Build the stacked-bar model from a breakdown payload. */
export function buildCostChartModel(data: DailyCostBreakdown): CostChartModel {
  const dates = data.days.map((d) => d.date);
  const xLabels = dates.map(formatDayLabel);

  const all: CostSeries[] = data.agents.map((agent) => ({
    agentId: agent.id,
    label: agent.name,
    data: data.days.map((day) => {
      const slice = day.byAgent.find((s) => s.agentId === agent.id);
      return slice ? slice.costUsd : 0;
    }),
  }));

  // Fold the tail (smallest spenders — `agents` is ordered biggest-first) into one
  // "Other" series so categorical hues are never cycled onto confusable pairs.
  let series = all;
  if (all.length > MAX_SERIES) {
    const kept = all.slice(0, MAX_SERIES - 1);
    const folded = all.slice(MAX_SERIES - 1);
    const other: CostSeries = {
      agentId: OTHER_SERIES_ID,
      label: `Other (${folded.length})`,
      data: folded.reduce<number[]>((acc, s) => addInto(acc, s.data), dates.map(() => 0)),
    };
    series = [...kept, other];
  }

  const totalUsd = data.days.reduce((sum, d) => sum + d.totalUsd, 0);

  return {
    dates,
    xLabels,
    series,
    totalUsd: Math.round(totalUsd * 1e6) / 1e6,
    isEmpty: totalUsd === 0,
  };
}
